import { useMemo, useRef } from "react"
import { useSyncRefValue } from "@/core/hooks"
import { useTableCtx } from "./TableContext"
import { Checkbox } from "antd"
import Icon from "../Icon"
import { css } from "twin.macro"
import { map, get, includes, isPlainObject } from "lodash"
import { clsx } from "@/utilities"
import { COL_SELECTION_WIDTH, isRowSelected } from "./helper"

function MemorizedRowRenderer({ columns, record, isSubRowHeader, ...props }) {
    const savedColumns = useRef(columns)
    const savedProps = useRef(props)

    useSyncRefValue(savedColumns, columns)
    useSyncRefValue(savedProps, props)

    const cells = useMemo(() => {
        const contents = {}
        const itemIndex = savedProps.current.itemIndex
        for (const { key, ...column } of columns) {
            let content = get(record, column.dataIndex) ?? null
            if (typeof column.render === "function") {
                content = column.render(content ?? record, record, itemIndex)
            }
            contents[key] = content
        }
        return contents
    }, [record, columns])

    return useMemo(() => {
        const columns = savedColumns.current
        const colWidthAsPercents = savedProps.current.colWidthAsPercents
        const onRowClick = savedProps.current.onRowClick

        return map(columns, ({ key, ...column }, index) => {
            const widthPercent = colWidthAsPercents[index + 1]
            const isClickDisabled =
                includes(column.className, "no-click-selection") || isSubRowHeader
            const content = cells[key]

            return (
                <div
                    key={key}
                    className={clsx("table-body-cell", "table-cell")}
                    css={css({
                        minWidth: column.width,
                        width: widthPercent + "%",
                        textAlign: column.align
                    })}
                    {...(!isClickDisabled && { onClick: onRowClick })}
                >
                    {content}
                </div>
            )
        })
    }, [cells, isSubRowHeader])
}

function ItemRenderer({ style, record, itemIndex, rowIndex, rowKey }) {
    const {
        columns,
        colWidthAsPercents,
        getItemSize,
        measure,
        isMeasured,
        isMeasuring,
        rowSelection,
        expandable
    } = useTableCtx()
    const { getCheckboxProps, onSelect } = rowSelection ?? {}
    const { disabled: isRowDisabled } = getCheckboxProps?.(record) ?? {}
    const isSelected = isRowSelected(rowKey, record, rowSelection)
    const rowHeight = getItemSize(rowIndex)
    const _isMeasuring = isMeasuring(rowIndex)
    const _isMeasured = isMeasured(rowIndex)

    const setRef = (ref) => measure(rowIndex, ref)

    const handleClick = (evt) => {
        evt.stopPropagation()
        if (!isRowDisabled) onSelect?.(record, !isSelected)
    }

    const rowClassName = useMemo(
        () =>
            clsx(
                "table-row",
                "table-body-row",
                isSelected && "table-row-selected",
                isRowDisabled && "table-row-disabled",
                expandable?.expandedRowClassName?.(record)
            ),
        [expandable, isRowDisabled, isSelected, record]
    )

    const isSubRowHeader = includes(rowClassName, "table-body-row-expanded-header")
    const isCheckboxDisplayed = typeof onSelect === "function"
    const isBtnExpandDisplayed = isPlainObject(expandable)
    const isNotExpandable = includes(rowClassName, "un-expandable")
    const isExpanded = useMemo(
        () => includes(expandable?.expandedRowKeys, rowKey),
        [rowKey, expandable?.expandedRowKeys]
    )

    return (
        <div
            css={css(style)}
            tw="transition-opacity"
            className="table-body-row-wrapper"
            style={{ opacity: _isMeasuring ? 0.05 : 1 }}
            data-measuring={_isMeasuring}
            data-measured-size={rowHeight}
        >
            <div
                ref={setRef}
                tw="flex transition-opacity"
                className={rowClassName}
                style={{ opacity: _isMeasured ? 1 : 0 }}
            >
                {isCheckboxDisplayed && (
                    <div
                        tw="center"
                        className="table-body-cell table-cell table-selection-column"
                        css={css({
                            width: colWidthAsPercents[0] + "%",
                            minWidth: COL_SELECTION_WIDTH
                        })}
                    >
                        {!isSubRowHeader && <Checkbox checked={isSelected} onClick={handleClick} />}
                    </div>
                )}
                <MemorizedRowRenderer
                    columns={columns}
                    record={record}
                    itemIndex={itemIndex}
                    isSubRowHeader={isSubRowHeader}
                    colWidthAsPercents={colWidthAsPercents}
                    onRowClick={handleClick}
                />
                {isBtnExpandDisplayed && (
                    <div
                        className={clsx("table-body-cell table-cell table-cell-arrow")}
                        data-expanded={isExpanded}
                        {...(!isNotExpandable && {
                            onClick: () => {
                                const set = new Set(expandable.expandedRowKeys)
                                set.has(rowKey) ? set.delete(rowKey) : set.add(rowKey)
                                expandable.onExpand?.(Array.from(set))
                            }
                        })}
                    >
                        <div>{!isNotExpandable && <Icon name="arrow_right" />}</div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ItemRenderer
